<template>
  <div
    v-if="incidents.length > 0"
    class="statuspage-widget"
  >
    <transition-group
      name="sc-fade"
      tag="div"
    >
      <AppStatuspageToast
        v-for="incident in incidents"
        :key="incident.id"
        :incident="incident"
        @close-incident="closeIncident"
      />
    </transition-group>
  </div>
</template>

<script>
import { defineAsyncComponent } from 'vue'
import { mapGetters } from 'vuex'

import DateTime from '@/utils/date-time.js'
import poll from '@/utils/poll.js'
import { SHIPPING_FETCH_CARRIERS_LAZILY } from '@/store/shipping/action.types.js'
import StatuspageService from '@/services/statuspage.service.js'

const MAX_NUMBER_OF_INCIDENTS_TO_SHOW = 3

/**
 * Maps Statuspage component names for carriers to their associated carrier codes.
 *
 * This manual mapping is necessary in order to avoid storing information like the carrier code
 * along with a component’s name on statuspage.io.
 */
const carrierCodeMap = {
  'Amazon': ['amazon'],
  'Bol': ['bol'],
  'Bpost': ['bpost'],
  'Bring': ['bring'],
  'BRT': ['brt'],
  'Chronopost': ['chronopost'],
  'Colis Privé': ['colisprive'],
  'Colissimo': ['colissimo'],
  'Colissimo EC2C': ['colissimo_ec2c'],
  'Correos Express': ['correos_express'],
  'Correos': ['correos'],
  'CTT Express': ['ctt_express'],
  'Delivengo': ['delivengo'],
  'Deutsche Post': ['dp'],
  'DHL (Germany)': ['dhl_de'],
  'DHL eCommerce': ['dhl_ecommerce'],
  'DHL Express': ['dhl_express'],
  'DHL Parcel (Benelux)': ['dhl'],
  'DHL Parcel UK': ['dhl_gb'],
  'DHL Parcel UK V2': ['dhl_parcel_gb'],
  'DHL Parcel Iberia': ['dhl_parcel_iberia'],
  'DPD AT': ['dpd_at'],
  'DPD FR': ['dpd_fr'],
  'DPD Local': ['dpd_local'],
  'DPD UK': ['dpd_gb'],
  'DPD': ['dpd'],
  'Fairsenden': ['fairsenden'],
  'FedEx': ['fedex'],
  'FedEx Crossborder': ['fedexcrossborder'],
  'Fietskoeriers': ['fietskoeriers'],
  'GLS ES': ['gls_es'],
  'GLS EU': ['gls_eu'],
  'GLS Italy': ['gls_it'],
  'GLS NL': ['gls_nl'],
  'GLS': ['gls_de'],
  'Glovo': ['glovo'],
  'Hermes DE': ['hermes_de'],
  'Hermes UK': ['hermes_gb'],
  'Hurby': ['hurby'],
  'InPost GB': ['inpost_gb'],
  'InPost IT': ['inpost_it'],
  'Lettre Suivie': ['lettresuivie'],
  'MHI': ['mhi'],
  'Mondial Relay': ['mondial_relay'],
  'MRW': ['mrw'],
  'Nacex': ['nacex'],
  'Parcelforce': ['parcelforce'],
  'Österreichische Post': ['postat'],
  'Poste Italiane': ['poste_italiane'],
  'Poste Italiane Delivery': ['poste_it_delivery'],
  'PostNL': ['postnl', 'postnl_fulfilment'],
  'Quicargo': ['quicargo'],
  'Red je Pakketje': ['rjp'],
  'Red je Pakketje V2': ['rjpv2'],
  'Royal Mail': ['royal_mail'],
  'Sandd': ['van_straaten'],
  'Sendcloud': null, // Not an actual carrier
  'SEUR': ['seur'],
  'Storeshippers': ['storeshippers'],
  'TIPSA': ['tipsa'],
  'TNT Italy': ['tnt_it'],
  'UPS': ['ups'],
  'Van Straaten Post': null, // Not in the carriers endpoint
  'Viatim': ['viatim'],
  'Yodel': ['yodel'],
  'Europaket': ['europaket'],
  'Geodis': ['geodis'],
  'Spring': ['spring'],
  'Evri International': ['evri_international'],
  'Asendia': ['asendia'],
  'Transforce': ['transforce'],
}

const currentDate = new Date()

export default {
  name: 'AppStatuspageWidget',

  pollDelayInMinutes: 5,

  // Sets the date to two days ago.
  incidentIsOldDate: new Date(currentDate.setDate(currentDate.getDate() - 2)),

  hoursToShowResolvedStatusFor: 2,

  components: {
    AppStatuspageToast: defineAsyncComponent(() => import('./AppStatuspageToast.vue')),
  },

  data() {
    return {
      incidents: [],
      dismissedIncidentStatuses: null,
    }
  },

  computed: {
    ...mapGetters(['user', 'carriers', 'settings']),

    userJoinedDate() {
      return new Date(this.user.date_joined)
    },

    /**
     * Returns a list of carrier names that are available to the user.
     *
     * @returns {string[]}
     */
    enabledCarrierCodes() {
      return this.carriers.filter(carrier => carrier.is_enabled).map(carrier => carrier.code)
    },

    storageKey() {
      return `dismissed-statuspage-incidents-${this.user.id}`
    },
  },

  async created() {
    await this.$store.dispatch(SHIPPING_FETCH_CARRIERS_LAZILY)
    this.initializeDismissedIncidents()

    if (!this.settings.django.IS_RUNNING_E2E) {
      this.pollStatuspageIncidents()
    }
  },

  methods: {
    /**
     * Retrieves a list of dismissed incident IDs and their statuses from localStorage.
     *
     * Intentionally doesn’t use our `getVariable` method due to a [bug][SC-18713]
     * where notifications would get un-dismissed upon switching accounts.
     *
     * [SC-18713]: https://sendcloud.atlassian.net/browse/SC-18713
     *
     * @returns {[string, StatuspageStatus][]}
     */
    getStoredDismissedIncidents() {
      let storagePayload

      try {
        storagePayload = window.localStorage.getItem(this.storageKey)
      } catch (error) {}

      return JSON.parse(storagePayload)
    },

    /**
     * Stores a list of dismissed incident IDs along with the status of the respective incident.
     *
     * @param {[string, StatuspageStatus][]} item
     */
    storeDismissedIncidents(item) {
      const storagePayload = JSON.stringify(item)
      try {
        window.localStorage.setItem(this.storageKey, storagePayload)
      } catch (error) {}
    },

    /**
     * Reads in a list of dismissed incident IDs from localStorage.
     * They are used to not show incidents to users who dismissed (closed) them.
     */
    initializeDismissedIncidents() {
      this.dismissedIncidentStatuses = new Map(this.getStoredDismissedIncidents())
    },

    /**
     * Starts off the polling process.
     */
    async pollStatuspageIncidents() {
      poll(this.updateIncidents, this.$options.pollDelayInMinutes * 60 * 1000).catch(() => {
        // Explicitly not handling errors throw by the poll function.
        // We use this behavior as a mechanism of stopping the polling process
        // in the case the underlying Statuspage.io endpoint can’t be reached (for example).
      })
    },

    /**
     * Updates the incidents data after querying the Statuspage API.
     */
    async updateIncidents() {
      const { data } = await StatuspageService.getIncidents()

      if (Array.isArray(data.incidents) && data.incidents.length > 0) {
        this.incidents = this.preProcessIncidents(data.incidents)
      }
    },

    /**
     * Preprocesses the incidents data.
     *
     * - Filters out all incidents that were previously dismissed.
     * - Limits the amount of incidents to be shown at any given time.
     * - Adds a `relativeTime` property to display time as “11 days ago”.
     *
     * @param {StatuspageIncident[]} incidents
     * @returns {Incident[]}
     */
    preProcessIncidents(incidents) {
      return (
        incidents
          .filter(this.incidentShouldBeShown)
          // Limit the amount of incidents that can be shown at any given time.
          .slice(0, MAX_NUMBER_OF_INCIDENTS_TO_SHOW)
          .map((incident) => {
            const serializedIncident = this.serializeIncident(incident)

            serializedIncident.relativeTime = DateTime.fromNow(incident.updated_at)

            return serializedIncident
          })
      )
    },

    /**
     * Filters incidents that should be shown to the user.
     *
     * The filter criteria are:
     *
     * 1. Incident was created after the user joined the platform.
     * 2. Incident is not too old.
     * 3. Incident was resolved within the last three hours.
     * 4. Incident with the same or older status was not dismissed before.
     * 5. Incident affects the user.
     *
     * @param {StatuspageIncident} incident
     * @returns {boolean}
     */
    incidentShouldBeShown(incident) {
      // 1. Incident was created before the user joined the platform.
      const incidentCreatedDate = new Date(incident.created_at)
      if (incidentCreatedDate < this.userJoinedDate) {
        return false
      }

      // 2. Incident is too old.
      const incidentUpdatedDate = new Date(incident.updated_at)
      if (incidentUpdatedDate < this.$options.incidentIsOldDate) {
        return false
      }

      // 3. Incident is resolved and was resolved more than three hours ago.
      if (incident.status === 'resolved' || incident.status === 'postmortem') {
        const resolvedDate = new Date(incident.resolved_at)
        const offsetHours = this.$options.hoursToShowResolvedStatusFor
        const resolvedIncidentTooOldDate = new Date(resolvedDate.setHours(resolvedDate.getHours() + offsetHours))

        if (currentDate < resolvedIncidentTooOldDate) {
          return false
        }
      }

      const dismissedStatus = this.dismissedIncidentStatuses.get(incident.id)

      // 4. Incident was dismissed before, and its current status is not newer.
      if (dismissedStatus !== undefined && this.statusIsOlderOrEqual(incident.status, dismissedStatus)) {
        return false
      }

      // 5. Incident doesn’t affect the user.
      // For example, a carrier component is affected that the user doesn’t have enabled.
      if (!this.userIsAffectedByIncident(incident)) {
        return false
      }

      return true
    },

    /**
     * Checks if a user is affected by an incident.
     *
     * An incident is affecting a user if:
     *
     * - the incident doesn’t list any components
     * - the incident lists atleast one component that is not carrier-specific
     * - the incident lists atleast one carrier-specific component
     *   including a carrier that is enabled by the user
     *
     * @param {StatuspageIncident}
     * @returns {boolean}
     */
    userIsAffectedByIncident(incident) {
      // If the incident doesn’t have any affected components,
      // we assume the incident is more general and thus, the user is potentially affected.
      if (incident.components.length === 0) {
        return true
      }

      const carrierPrefix = 'Carriers - '

      const isNotCarrierSpecific = incident.incident_updates.some((incidentUpdate) => {
        if (!incidentUpdate.affected_components) {
          return true
        }

        return incidentUpdate.affected_components.some((component) => {
          return !component.name.startsWith(carrierPrefix)
        })
      })

      if (isNotCarrierSpecific) {
        return true
      }

      // Get names of affected components which are carrier-related
      const affectedComponentNames = new Set()
      for (const incidentUpdate of incident.incident_updates) {
        incidentUpdate.affected_components.forEach((component) => {
          const prefixPos = component.name.indexOf(carrierPrefix)

          if (prefixPos === 0) {
            const componentName = component.name.substring(prefixPos + carrierPrefix.length).trim()
            affectedComponentNames.add(componentName)
          }
        })
      }

      for (const componentName of affectedComponentNames) {
        const statuspageComponentCodes = carrierCodeMap[componentName]

        if (statuspageComponentCodes === null) {
          continue
        }

        if (
          Array.isArray(statuspageComponentCodes) &&
          statuspageComponentCodes.some(code => this.enabledCarrierCodes.includes(code))
        ) {
          return true
        }

        // This is a mechanism for allowing incidents to be shown for carriers that
        // haven’t yet been added to the `carrierCodeMap`.
        if (statuspageComponentCodes === undefined) {
          return true
        }
      }

      return false
    },

    /**
     * Serializes a Statuspage incident object
     * so that it only contains properties required to render it.
     *
     * @param {StatuspageIncident} incident The incoming incident object.
     * @returns {Incident} An incident object containg the subset of required properties
     * of the incoming object.
     */
    serializeIncident({ id, name, status, impact, shortlink }) {
      return { id, name, status, impact, shortlink, relativeTime: null }
    },

    /**
     * Removes an incident from the page.
     *
     * @param {Incident} incidentToClose
     */
    closeIncident(incidentToClose) {
      this.dismissIncident(incidentToClose)

      const deleteIndex = this.incidents.findIndex(incident => incident.id === incidentToClose.id)
      if (deleteIndex > -1) {
        this.incidents.splice(deleteIndex, 1)
      }
    },

    /**
     * Persistently dismisses an incident by storing its ID in localStorage.
     * Upon populating the page with new incidents, dismissed ones are ignored.
     *
     * @param {Incident} incident
     */
    dismissIncident(incident) {
      this.dismissedIncidentStatuses.set(incident.id, incident.status)

      this.storeDismissedIncidents(Array.from(this.dismissedIncidentStatuses.entries()))
    },

    /**
     * Comparison function for Statuspage statuses. Returns true for “older” or equal statuses.
     *
     * Statuses are ordered like this from oldest to newest:
     *
     * - investigating
     * - identified
     * - monitoring
     * - resolved
     * - postmortem
     *
     * @param {StatuspageStatus} statusA
     * @param {StatuspageStatus} statusB
     * @returns {boolean} true if `statusA` is “older” than `statusB` or equal; false otherwise.
     */
    statusIsOlderOrEqual(statusA, statusB) {
      const order = ['investigating', 'identified', 'monitoring', 'resolved', 'postmortem']
      return order.indexOf(statusA) <= order.indexOf(statusB)
    },
  },
}

/**
 * @typedef StatuspageStatus
 * @type {'investigating'|'identified'|'monitoring'|'resolved'|'postmortem'}
 */

/**
 * @typedef StatuspageImpactLevel
 * @type {'none'|'minor'|'major'|'critical'}
 */

/**
 * @typedef StatuspageIncident
 * @type {Object}
 * @property {string} id
 * @property {string} name
 * @property {StatuspageStatus} status
 * @property {StatuspageImpactLevel} impact
 * @property {string} created_at
 * @property {string} started_at
 * @property {string} updated_at
 * @property {string} monitoring_at
 * @property {string} resolved_at
 * @property {string} shortlink
 * @property {string} page_id
 * @property {Object[]} incident_updates
 * @property {Object[]} components
 */

/**
 * @typedef Incident
 * @type {Object}
 * @property {string} id
 * @property {string} name
 * @property {StatuspageStatus} status
 * @property {StatuspageImpactLevel} impact
 * @property {string} relativeTime
 */
</script>

<style lang="scss" scoped>
.statuspage-widget {
  pointer-events: none;
  position: fixed;
  z-index: 200;
  bottom: 0;
  right: 0;
  left: 0;
}

@include breakpoint-min-width(600px) {
  .statuspage-widget {
    margin-bottom: 20px;
  }

  // The div is related to Vue’s list transition
  .statuspage-widget > div > *:not(:first-child) {
    margin-top: 10px;
  }
}

// Vue transitions
.sc-fade-enter-active,
.sc-fade-leave-active {
  position: absolute;

  --fade-transition-timing: ease-out;
}

.sc-fade-move {
  transition: opacity 0.2s ease-out, transform 0.5s;
}
</style>
